import React from 'react';
import { useSelector } from 'react-redux';
import L from 'leaflet';
import PieChart from 'react-minimal-pie-chart';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { Button, Heading, Pane, Strong, Text } from 'evergreen-ui';
import { Link } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import 'react-leaflet-markercluster/dist/styles.min.css';
import { Marker, Popup, useMap, useMapEvent } from 'react-leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import { useSelectedMapFilter } from 'Features/Map/useSelectedMapFilter';
import { datasourceStatusesSelector, stationsFullSelector } from 'Features/App/Dictionaries/selectors';
import { filteredStationsIdsSelector, stationDatasourceIdsFromFilteredSelector } from 'Features/Filters/Stations';
import { DANGER_COLORS, DATASOURCE_FILTER_STATUSES, NONE_COLORS, SUCCESS_COLORS, WARNING_COLORS, getColor, getStatusByDatasourceDangerMode, getStatusByDatasourceDataReceived, getStatusByDatasourceEnableMode, getStatusByDatasourceStormMode } from 'Features/Map/utilts';
import { useGetAllMeteoCodesQuery } from 'Services/api/meteoCodesApi';
import useMapSelectedStation from './useMapSelectedStation';
import useMapEnableClustering from './useMapEnableClustering';
import { mapMeteocodeDataSelector } from './mapSlice';
import { MapPie } from './MapPie/MapPie';

function createClusterCustomIcon(cluster) {
  const childCount = cluster.getChildCount();

  let c = ' marker-cluster-';
  if (childCount < 10) {
    c += 'small';
  } else if (childCount < 100) {
    c += 'medium';
  } else {
    c += 'large';
  }

  return new L.DivIcon({
    html: `<div><span>${childCount}</span></div>`,
    className: `marker-cluster${c}`,
    iconSize: new L.Point(40, 40),
  });
}

// TODO: вынести потом в отдельный файл
function ValuesMarker({ values = [], size = undefined, singleValue, zoom }) {
  if (singleValue) {
    return (
      <div style={{
        width: 36,
        height: 36,
        background: values[0]?.value ? '#9c70cd' : '#9b70cd80',
        border: '1px solid #000000e8',
        borderRadius: '50%',
        display: 'grid',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      >
        {values.length > 0 && (
          <p style={{ color: 'white', zIndex: 2, textAlign: 'center' }}>
            {`${values[0].value} ${values[0].value && values[0].unit}`}
          </p>
        )}
      </div>
    );
  }
  return <MapPie values={zoom <= 8 ? [] : values} size={size} />;
}

function createMeasureIcon(values, singleValue, zoom) {
  const size = singleValue ? 36 : 128;
  const iconSettings = {
    mapIconUrl: renderToStaticMarkup(<ValuesMarker values={values} size={size} singleValue={singleValue} zoom={zoom} />),
  };
  // eslint-disable-next-line new-cap
  return new L.divIcon({
    className: 'leaflet-data-marker',
    html: L.Util.template(iconSettings.mapIconUrl, iconSettings),
    iconAnchor: [size / 2, size / 2],
    iconSize: [size, size],
    popupAnchor: [0, -size / 4],
  });
}

function createStationIcon(data, hasBadStatus) {
  // надо поиграться с позиционированием
  const iconSettings = {
    mapIconUrl: renderToStaticMarkup(
      <div style={{
        position: 'relative',
      }}
      >
        {hasBadStatus && <div className="pulse" />}
        <PieChart
          cx={50}
          cy={50}
          data={data}
          label={false}
        // labelPosition={50}
          lengthAngle={360}
          lineWidth={100}
          style={{
            border: '1px solid #a8a8a8e7',
            borderRadius: '100px',
          }}
          paddingAngle={0}
          radius={50}
          rounded={false}
          startAngle={0}
          viewBoxSize={[
            25,
            25,
          ]}
        />
      </div>
      ,
    ),
  };
  const size = 25;

  // eslint-disable-next-line new-cap
  return new L.divIcon({
    className: 'leaflet-data-marker',
    html: L.Util.template(iconSettings.mapIconUrl, iconSettings),
    iconAnchor: [size / 2, size / 2],
    iconSize: [size, size],
    popupAnchor: [0, -(size / 2) / 2],
  });
}

export default function MapMarkerCluster() {
  const { isEnableClustering } = useMapEnableClustering();
  const values = useSelector(mapMeteocodeDataSelector);
  const stationsFullDictionaries = useSelector(stationsFullSelector);
  const filteredStationsIds = useSelector(filteredStationsIdsSelector);
  const [currentZoom, setCurrentZoom] = React.useState(4);

  const { data: meteocodes = [] } = useGetAllMeteoCodesQuery();
  const events = useMapEvent('zoomend', () => setCurrentZoom(events.getZoom()));

  const filteredStations = React.useMemo(() => {
    if (filteredStationsIds.length === 0) return [];
    return filteredStationsIds.map((stationId) => stationsFullDictionaries[stationId]);
  }, [filteredStationsIds, stationsFullDictionaries]);

  const meteocodeDictionary = React.useMemo(() => {
    return meteocodes.reduce((acc, item) => {
      acc[item.meteoCodeString] = item;
      return acc;
    }, {});
  }, [meteocodes]);

  const valuesDicrionary = React.useMemo(() => {
    if (Object.keys(meteocodeDictionary).length > 0) {
      return values.reduce((acc, item) => {
        const stationId = item.request.sourceId;
        const vals = acc[stationId];
        const res = {
          meteocode: item.request.meteoCode,
          value: item.response?.valueRound || item.response?.value || '',
          unit: meteocodeDictionary[item.request.meteoCode]?.unit,
          meteocodeName: meteocodeDictionary[item.request.meteoCode]?.name,
        };
        if (vals) {
          vals.push(res);
        } else {
          acc[stationId] = [res];
        }
        return acc;
      }, {});
    }
    return {};
  }, [meteocodeDictionary, values]);

  return (
    <MarkerClusterGroup
      key={isEnableClustering}
      iconCreateFunction={createClusterCustomIcon}
      animate={false}
      zoomToBoundsOnClick
      spiderfyOnMaxZoom
      disableClusteringAtZoom={isEnableClustering ? 18 : 0}
      removeOutsideVisibleBounds
    >
      {
        filteredStations.map(({ station }) => (
          <MapMarker key={station.id} station={station} values={valuesDicrionary[station.id]} zoom={currentZoom} />
        ))
      }
    </MarkerClusterGroup>
  );
}

function MapMarker({ station, values, zoom }) {
  const map = useMap();
  const { selectStation } = useMapSelectedStation();
  const datasourceIds = useSelector(stationDatasourceIdsFromFilteredSelector(station.id));
  const datasourceStatusesDictionaries = useSelector(datasourceStatusesSelector);
  const { selectedMapFilter } = useSelectedMapFilter();
  const { markerParts, hasBadStatus } = React.useMemo(() => {
    const value = 100 / (datasourceIds.length || 1);
    let isContainBadStatus = false;
    const parts = [];
    datasourceIds.forEach((datasourceId) => {
      const datasource = datasourceStatusesDictionaries[datasourceId];
      if (datasource) {
        if (selectedMapFilter === 'MODE') {
          const { status, hasBadStatus } = getStatusByDatasourceStormMode(datasource.stormMode);
          if (hasBadStatus && !isContainBadStatus) {
            isContainBadStatus = hasBadStatus;
          }
          parts.push({ status, value });
          return;
        }
        if (selectedMapFilter === 'ENABLE') {
          const { status, hasBadStatus } = getStatusByDatasourceEnableMode(datasource.enableMode);
          if (hasBadStatus && !isContainBadStatus) {
            // если для источника в указан статус BAD, то ставим true один раз для всех markerParts
            isContainBadStatus = hasBadStatus;
          }
          parts.push({ status, value });
          return;
        }
        if (selectedMapFilter === 'DATA_RECEIVED') {
          const { status, hasBadStatus } = getStatusByDatasourceDataReceived(datasource.dataReceived);
          if (hasBadStatus && !isContainBadStatus) {
          // если для источника в указан статус BAD, то ставим true один раз для всех markerParts
            isContainBadStatus = hasBadStatus;
          }
          parts.push({ status, value });
          return;
        }
        if (selectedMapFilter === 'PHENOMENON') {
          const { status, hasBadStatus } = getStatusByDatasourceDangerMode(datasource.dangerMode);
          if (hasBadStatus && !isContainBadStatus) {
            // если для источника в указан статус BAD, то ставим true один раз для всех markerParts
            isContainBadStatus = hasBadStatus;
          }
          parts.push({ status, value });
        }
      }
      parts.push({ status: DATASOURCE_FILTER_STATUSES.NONE, value });
    });
    if (parts.length === 0) {
      parts.push({
        status: DATASOURCE_FILTER_STATUSES.NONE,
        value,
      });
    }

    const isEvenArray = parts.length % 2 === 0;
    parts.forEach((obj, index, arr) => {
      if (obj.status === DATASOURCE_FILTER_STATUSES.GOOD) {
        Object.assign(obj, { color: getColor({ palette: SUCCESS_COLORS, index, arrayLength: arr.length, isEvenArray }) });
        return;
      }

      if (obj.status === DATASOURCE_FILTER_STATUSES.BAD) {
        Object.assign(obj, { color: getColor({ palette: DANGER_COLORS, index, arrayLength: arr.length, isEvenArray }) });
        return;
      }
      if (obj.status === DATASOURCE_FILTER_STATUSES.NONE) {
        Object.assign(obj, { color: getColor({ palette: NONE_COLORS, index, arrayLength: arr.length, isEvenArray }) });
        return;
      }
      if (obj.status === DATASOURCE_FILTER_STATUSES.WARNING) {
        Object.assign(obj, { color: getColor({ palette: WARNING_COLORS, index, arrayLength: arr.length, isEvenArray }) });
      }
    });

    return { markerParts: parts, hasBadStatus: isContainBadStatus };
  }, [datasourceIds, datasourceStatusesDictionaries, selectedMapFilter]);

  return (
    <Marker
      key={station.id}
      zIndexOffset={hasBadStatus ? 1000 : 0}
      icon={(selectedMapFilter === 'METEOCODE' || selectedMapFilter === 'COMMON_METEOCODES')
        ? createMeasureIcon(values, selectedMapFilter === 'METEOCODE', zoom)
        : createStationIcon(markerParts, hasBadStatus, values)}
      position={[station.latitude || 0, station.longitude || 0]}
      eventHandlers={{
        click: () => {
          map.setView([station.latitude || 0, station.longitude || 0], undefined, { animate: false });
          selectStation(station);
        },
      }}
    >
      <MarkerPopup station={station} />
    </Marker>
  );
}

const buttonLinks = (stationId) => [{
  to: `/stations/${stationId}/profile`,
  label: 'Профиль',
}, {
  to: `/stations/${stationId}/passport`,
  label: 'Паспорт',
}, {
  to: `/stations/${stationId}/datasources`,
  label: 'Источники данных',
}, {
  to: `/stations/${stationId}/telegrams`,
  label: 'Телеграммы',
}, {
  to: `/stations/${stationId}/messages`,
  label: 'Сообщения',
}, {
  to: `/stations/${stationId}/timeseries-data`,
  label: 'Данные',
}];

const useStyles = createUseStyles({
  tableStripe: {
    '& div:nth-child(odd)': {
      backgroundColor: 'rgba(67, 90, 111, 0.06)',
    },
  },
});

function MarkerPopup({ station }) {
  const classes = useStyles();
  const { id, name, idx, altitude, latitude, longitude } = station;

  const links = React.useMemo(() => buttonLinks(id), [id]);

  return (
    <Popup
      autoPan={false}
      maxWidth="auto"
    >
      <Pane display="grid" gridTemplateRows="max-content 1fr max-content" rowGap=".5rem">
        <Heading>{name}</Heading>
        <Pane className={classes.tableStripe}>
          <MarkerPopupRowValue label="С/индекс" value={idx || '-'} />
          <MarkerPopupRowValue label="Учреждение" value="-" />
          <MarkerPopupRowValue label="Тип" value="-" />
          <MarkerPopupRowValue label="Высота" value={altitude || '-'} />
          <MarkerPopupRowValue label="Широта" value={latitude || '-'} />
          <MarkerPopupRowValue label="Долгота" value={longitude || '-'} />
        </Pane>
        <MarkerPopupLinks links={links} />
      </Pane>
    </Popup>
  );
}

function MarkerPopupRowValue({ label, value }) {
  return (
    <Pane
      display="grid"
      gridTemplateColumns="1fr .5fr"
      padding=".5rem"
    >
      <Strong>{label}</Strong>
      <Text>{value}</Text>
    </Pane>
  );
}

function MarkerPopupLinks({ links = [] }) {
  return (
    <Pane display="grid" gridTemplateColumns="repeat(3, 150px)">
      {
      links.map(({ to, label }) => (
        <Button
          appearance="minimal"
          whiteSpace="nowrap"
          justifyContent="center"
          width="100%"
          key={to}
          is={Link}
          to={to}
        >
          {label}
        </Button>
      ))
    }
    </Pane>
  );
}
